import { useLayoutEffect, useRef, useState } from "react";
import {
  ArrowBendUpLeft, PencilSimple, Copy, Trash, Star, PushPin, Info,
  ArrowsClockwise, ArrowClockwise, Translate, ShareNetwork,
} from "@phosphor-icons/react";

function MenuItem({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button onClick={onClick}
            className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left hover:bg-[var(--bg-sidebar)] ${danger ? "text-[var(--danger)]" : ""}`}
            data-testid={testid}>
      <Icon size={14} /> {label}
    </button>
  );
}

/**
 * Dropdown context-menu shown when the user clicks the kebab on a message bubble.
 *
 * iter135 P1 — smart positioning. Measures the viewport at mount and
 * flips the menu to the side/direction with the most available space
 * (right → left → above → below) so it never overflows off-screen.
 * Approach mirrors Teams / Slack / WhatsApp Web.
 */
export default function MessageMenu({
  msg, starredByMe, canEdit, canRecall, canDeleteForAll,
  onReply, onForward, onTranslate, onCopy, onStartEdit, onToggleStar, onTogglePin, onShowInfo,
  onDelForMe, onRecall, onDelForAll, menuRef,
}) {
  const localRef = useRef(null);
  const [pos, setPos] = useState({ vertical: "below", horizontal: "left" });

  useLayoutEffect(() => {
    const el = localRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const MARGIN = 8;

    // The menu is anchored to the message bubble's origin. Detect
    // overflow and flip along the axis with the least available space.
    const overflowsRight = rect.right + MARGIN > vw;
    const overflowsBottom = rect.bottom + MARGIN > vh;
    const spaceAbove = rect.top;
    const spaceBelow = vh - rect.bottom;

    const horizontal = overflowsRight ? "right" : "left";
    // Prefer below; only flip to above when we actually don't fit AND there's
    // clearly more room upward.
    const vertical = overflowsBottom && spaceAbove > spaceBelow ? "above" : "below";
    setPos({ vertical, horizontal });
  }, []);

  // Compose the anchor classes based on the measured direction. Keeps the
  // menu inside the same `absolute` container so it dismisses on outside
  // click via the parent's menuRef guard.
  const anchor = [
    pos.vertical === "above" ? "bottom-full mb-1" : "top-full mt-1",
    pos.horizontal === "right" ? "right-0" : "left-0",
  ].join(" ");

  return (
    <div
      ref={(node) => {
        localRef.current = node;
        if (menuRef) menuRef.current = node;
      }}
      className={`absolute z-30 bg-white border border-[var(--border)] shadow-lg min-w-[180px] rounded max-h-[70vh] overflow-y-auto ${anchor}`}
      data-testid={`msg-context-menu-${msg.id}`}
      data-position={`${pos.vertical}-${pos.horizontal}`}
    >
      <MenuItem icon={ArrowBendUpLeft} label="Reply" onClick={onReply} testid={`menu-reply-${msg.id}`} />
      <MenuItem icon={ShareNetwork} label="Forward" onClick={onForward} testid={`menu-forward-${msg.id}`} />
      <MenuItem icon={Translate} label="Translate" onClick={onTranslate} testid={`menu-translate-${msg.id}`} />
      <MenuItem icon={Copy} label="Copy" onClick={onCopy} testid={`menu-copy-${msg.id}`} />
      {canEdit && <MenuItem icon={PencilSimple} label="Edit" onClick={onStartEdit} testid={`menu-edit-${msg.id}`} />}
      <MenuItem icon={Star} label={starredByMe ? "Unstar" : "Star"} onClick={onToggleStar} testid={`menu-star-${msg.id}`} />
      <MenuItem icon={PushPin} label={msg.pinned ? "Unpin" : "Pin"} onClick={onTogglePin} testid={`menu-pin-${msg.id}`} />
      <MenuItem icon={Info} label="Info" onClick={onShowInfo} testid={`menu-info-${msg.id}`} />
      <div className="border-t border-[var(--border)]" />
      <MenuItem icon={Trash} label="Delete for me" onClick={onDelForMe} danger testid={`menu-delete-me-${msg.id}`} />
      {canRecall && <MenuItem icon={ArrowClockwise} label="Recall (unread)" onClick={onRecall} danger testid={`menu-recall-${msg.id}`} />}
      {canDeleteForAll && <MenuItem icon={ArrowsClockwise} label="Delete for everyone" onClick={onDelForAll} danger testid={`menu-delete-all-${msg.id}`} />}
    </div>
  );
}
